// S5 · Step 3 — the commit bar (Module 02 §4 S5, §3.4), on the design-system
// §3.7 CommitBar grammar (UX-1, D-107).
//
// Three jobs, unchanged in substance from the certified module-02 machinery:
//  1. Say what Commit will do — a LIVE plain-English sentence ("This commit
//     will create 1 department (ОТК) and 2 work centers (…)"), the honest
//     recorded-not-created line for machines (Module 08 unwired), and the
//     committed-nodes-missing panel. `confirm_deletions` defaults to false and
//     archiving takes a tick AND a confirmation: deletions are never implicit.
//  2. Run `POST /v1/onboarding/plant-map/commit` and report the result exactly
//     — created/updated/archived counts and the `pending[]` entities whose
//     owning module is not wired (never shown as created).
//  3. Explain every refusal in its own terms (409 ENTITY_IN_USE per node, 422
//     DUPLICATE_CODE, 503 RESOURCE_BUSY, 409 ALREADY_LIVE).
//
// The commit surface renders ONLY when the draft differs from the committed
// plant (isNoOpDiff) — §UX-1 state rules; the sentence is non-empty exactly
// when Commit is offered.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../components/button";
import { CommitBar } from "../../../../components/structure";
import { useCommitPlantMap } from "../../hooks/onboarding-queries";
import { useTerm, toTerminologyLocale } from "../../hooks/terminology";
import { WizardDialog } from "../../components/wizard-dialog";
import { WizardProblemNotice } from "../../components/wizard-notices";
import { isCode, refusedNodes, WIZARD_PROBLEM_CODES } from "../../lib/wizard-problems";
import {
  isNoOpDiff,
  toCommitDraft,
  type PlantDiff,
  type PlantDraft,
  type PlantNodeRef,
} from "./plant-draft";

type S = components["schemas"];

/** What a successful commit hands the screen for the §3.8 success banner. */
export interface CommitSuccessInfo {
  readonly createdNodes: readonly PlantNodeRef[];
  readonly restoredNodes: readonly PlantNodeRef[];
  readonly response: S["PlantMapCommitResponse"];
}

export interface CommitPanelProps {
  draft: PlantDraft;
  diff: PlantDiff;
  /** False when the draft has blocking problems (the client mirror of 422). */
  committable: boolean;
  /** §7: `onboarding.manage_wizard` — group_admin / facility_admin. */
  canCommit: boolean;
  /** The tenant is live: every wizard mutation answers 409 ALREADY_LIVE. */
  frozen: boolean;
  /** The committed tree could not be read, so deletions cannot be shown. */
  deletionsUnavailable: boolean;
  /** Refresh the committed tree after a successful commit. */
  onCommitted: () => void;
  /** UX-1 state 4: the screen renders the persistent success banner. */
  onSuccess?: ((info: CommitSuccessInfo) => void) | undefined;
  /** "Discard draft" — reload the draft from the last saved payload. */
  onDiscard?: (() => void) | undefined;
}

/** Compose the name list for one entity class — "(Экструзия-3, Стенд-1)". */
function names(nodes: readonly PlantNodeRef[], kind: PlantNodeRef["kind"]): string {
  return allNames(nodes.filter((n) => n.kind === kind));
}

/** The same name list, across kinds — the restore clause names every kind at
 * once because the server's answer counts them together, under `updated`. */
function allNames(nodes: readonly PlantNodeRef[]): string {
  const list = nodes.map((n) => (n.name.length > 0 ? n.name : n.path));
  if (list.length === 0) return "";
  const shown = list.slice(0, 4).join(", ");
  return list.length > 4 ? `${shown}, …` : shown;
}

export function CommitPanel({
  draft,
  diff,
  committable,
  canCommit,
  frozen,
  deletionsUnavailable,
  onCommitted,
  onSuccess,
  onDiscard,
}: CommitPanelProps) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  // The locale the names in this draft were AUTHORED in — the only side that
  // knows it, and what decides which `name_i18n` slot an edit lands in (§3.4).
  const locale = toTerminologyLocale(i18n.language);
  const commit = useCommitPlantMap();
  const [confirmDeletions, setConfirmDeletions] = useState(false);
  const [askingDelete, setAskingDelete] = useState(false);

  const editable = canCommit && !frozen;
  const absent = diff.absent;
  const wantsDeletions = confirmDeletions && absent.length > 0;
  const noOp = isNoOpDiff(diff);

  function run(withDeletions: boolean) {
    setAskingDelete(false);
    // Captured at CLICK time: `diff` is recomputed against the refetched tree
    // the moment the commit lands, at which point the created nodes are matched
    // nodes and the list is empty.
    const createdNodes = diff.createdNodes;
    const restoredNodes = diff.restoredNodes;
    commit.mutate(
      {
        draft: toCommitDraft(draft, locale),
        ...(withDeletions ? { confirm_deletions: true } : {}),
      },
      {
        onSuccess: (response) => {
          onCommitted();
          onSuccess?.({ createdNodes, restoredNodes, response });
        },
      },
    );
  }

  function onCommitClick() {
    if (wantsDeletions) {
      // A destructive action is never one click away (§3.4).
      setAskingDelete(true);
      return;
    }
    run(false);
  }

  const refused = refusedNodes(commit.error);
  const duplicate = isCode(commit.error, WIZARD_PROBLEM_CODES.duplicateCode);

  // ---- the live plain-English sentence -------------------------------------
  const parts: string[] = [];
  if (diff.create.facilities > 0) {
    parts.push(
      t("onboarding.plant.sentence.facilities", {
        count: diff.create.facilities,
        facility: term("facility"),
        names: names(diff.createdNodes, "facility"),
      }),
    );
  }
  if (diff.create.departments > 0) {
    parts.push(
      t("onboarding.plant.sentence.departments", {
        count: diff.create.departments,
        department: term("department"),
        names: names(diff.createdNodes, "department"),
      }),
    );
  }
  if (diff.create.work_centers > 0) {
    parts.push(
      t("onboarding.plant.sentence.workCenters", {
        count: diff.create.work_centers,
        workCenter: term("work_center"),
        names: names(diff.createdNodes, "work_center"),
      }),
    );
  }
  if (diff.create.storage_locations > 0) {
    parts.push(t("onboarding.plant.sentence.storage", { count: diff.create.storage_locations }));
  }
  // The restore clause is its OWN sentence, never a `create` part — the server
  // un-archives these and counts them under `updated` (§3.4).
  const restoreCount =
    diff.restore.facilities + diff.restore.departments + diff.restore.work_centers;
  const restoreClause =
    restoreCount > 0
      ? t("onboarding.plant.sentence.restore", {
          count: restoreCount,
          names: allNames(diff.restoredNodes),
        })
      : "";
  // Its OWN sentence too, and for the same reason as `restore`: the server
  // counts a rename under `updated`, never `created`. Naming these is also the
  // only warning the user gets before a name is overwritten.
  const editedClause =
    diff.edited.length > 0
      ? t("onboarding.plant.sentence.edited", {
          count: diff.edited.length,
          names: allNames(diff.edited),
        })
      : "";
  // "Matched and left unchanged" must EXCLUDE the ones about to be rewritten —
  // a node cannot be both untouched and updated in the same sentence.
  const matchedCount =
    diff.matched.facilities +
    diff.matched.departments +
    diff.matched.work_centers -
    diff.edited.length;
  const clauses: string[] = [];
  if (parts.length > 0) {
    clauses.push(t("onboarding.plant.sentence.create", { parts: joinList(parts, t) }));
  }
  if (restoreClause.length > 0) clauses.push(restoreClause);
  if (editedClause.length > 0) clauses.push(editedClause);
  if (clauses.length === 0 && absent.length > 0) {
    clauses.push(t("onboarding.plant.sentence.onlyDeletions", { count: absent.length }));
  }
  if (clauses.length > 0 && matchedCount > 0) {
    clauses.push(t("onboarding.plant.sentence.matched", { count: matchedCount }));
  }
  const sentence = clauses.join(" ");

  // §UX-1 state rules: no commit surface until the draft differs from the
  // committed plant. A draft with BLOCKING problems still shows the bar (with
  // the reason and Commit disabled) — a blank code zeroes the diff, and hiding
  // the surface at that moment would hide the very explanation the user needs.
  if (noOp && committable) return null;

  const hint = frozen
    ? t("onboarding.plant.commitFrozen")
    : !canCommit
      ? t("onboarding.plant.commitReadOnly")
      : !committable
        ? t("onboarding.plant.commitBlocked")
        : t("onboarding.plant.commitValidated");

  return (
    <section className="plant-commit" aria-label={t("onboarding.plant.commitHeading")}>
      {/* The recorded-not-created line does NOT live here. Machines are never a
          reason to commit, so this surface hides the moment the plant matches —
          and the machine counts the user typed would have gone with it. The
          screen renders `DeferredNote` beside the tree instead, where it stays
          true for as long as the draft carries the counts (surface review F4). */}
      {absent.length > 0 ? (
        <div className="plant-absent" data-testid="plant-absent">
          <p className="plant-panel-title">{t("onboarding.plant.absentTitle")}</p>
          <p className="plant-panel-body">{t("onboarding.plant.absentBody")}</p>
          <ul className="plant-list">
            {absent.map((node) => (
              <li key={`${node.kind}:${node.path}`}>
                <code>{node.path}</code> —{" "}
                {t(`onboarding.plant.absentKind.${node.kind}`, {
                  facility: term("facility"),
                  department: term("department"),
                  workCenter: term("work_center"),
                })}
              </li>
            ))}
          </ul>
          {editable ? (
            <>
              <label className="plant-confirm-delete">
                <input
                  type="checkbox"
                  checked={confirmDeletions}
                  onChange={(e) => {
                    setConfirmDeletions(e.target.checked);
                  }}
                />
                <span>{t("onboarding.plant.confirmDeletionsLabel", { count: absent.length })}</span>
              </label>
              <p className="plant-hint">{t("onboarding.plant.confirmDeletionsHint")}</p>
            </>
          ) : null}
        </div>
      ) : null}

      {askingDelete ? (
        <WizardDialog
          testId="plant-delete-confirm"
          destructive
          title={t("onboarding.plant.deleteConfirmTitle", { count: absent.length })}
          confirmLabel={t("onboarding.plant.deleteConfirmCta")}
          cancelLabel={t("onboarding.plant.cancel")}
          busy={commit.isPending}
          onConfirm={() => {
            run(true);
          }}
          onCancel={() => {
            setAskingDelete(false);
          }}
        >
          <p>{t("onboarding.plant.deleteConfirmBody")}</p>
          <ul className="plant-list">
            {absent.map((node) => (
              <li key={`confirm:${node.kind}:${node.path}`}>
                <code>{node.path}</code>
              </li>
            ))}
          </ul>
        </WizardDialog>
      ) : null}

      <CommitBar
        sentence={
          <span data-testid="plant-diff">
            {sentence.length > 0 ? sentence : t("onboarding.plant.diffNothing")}
          </span>
        }
        hint={hint}
        actions={
          <>
            {onDiscard !== undefined && editable ? (
              <Button
                onClick={onDiscard}
                disabled={commit.isPending}
                data-testid="plant-discard-draft"
              >
                {t("onboarding.plant.discardDraft")}
              </Button>
            ) : null}
            <Button
              variant="primary"
              busy={commit.isPending}
              disabled={!editable || !committable || sentence.length === 0}
              onClick={onCommitClick}
            >
              {commit.isPending
                ? t("onboarding.plant.commitWorking")
                : t("onboarding.plant.commitCta")}
            </Button>
          </>
        }
      />
      {deletionsUnavailable ? (
        <p className="plant-hint">{t("onboarding.plant.existingForbiddenBody")}</p>
      ) : null}

      {commit.error !== null ? (
        refused.length > 0 ? (
          <div className="plant-problems" role="alert" data-testid="plant-refused">
            <p className="plant-panel-title">{t("onboarding.plant.refusedTitle")}</p>
            <p className="plant-panel-body">{t("onboarding.plant.refusedBody")}</p>
            <ul className="plant-list">
              {refused.map((node) => (
                <li key={node.path}>
                  <code>{node.path}</code>
                  <br />
                  {/* `refused[].reason` is English prose with no i18n key on the
                      wire, so the EXPLANATION is a translated sentence and the
                      server's own words sit behind a technical disclosure. */}
                  <span className="plant-refused-reason">
                    {t("onboarding.plant.refusedReasonGeneric")}
                  </span>
                  {node.reason.length > 0 ? (
                    <details className="plant-technical">
                      <summary>{t("onboarding.plant.refusedReasonLabel")}</summary>
                      <code>{node.reason}</code>
                    </details>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : duplicate ? (
          <div className="plant-problems" role="alert" data-testid="plant-duplicate">
            <p className="plant-panel-title">{t("onboarding.plant.duplicateTitle")}</p>
            <p className="plant-panel-body">
              {t("onboarding.plant.duplicateBody", {
                facility: term("facility"),
                department: term("department"),
                workCenter: term("work_center"),
              })}
            </p>
          </div>
        ) : (
          <WizardProblemNotice
            error={commit.error}
            onRetry={() => {
              commit.reset();
            }}
            titleKey="onboarding.plant.commitFailedTitle"
            detailKey="onboarding.plant.commitFailedDetail"
          />
        )
      ) : null}
    </section>
  );
}

/** Join sentence parts with the locale's list glue ("…, … and …"). */
function joinList(parts: readonly string[], t: ReturnType<typeof useTranslation>["t"]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  const head = parts.slice(0, -1).join(t("onboarding.plant.sentence.comma"));
  return `${head}${t("onboarding.plant.sentence.and")}${parts[parts.length - 1] ?? ""}`;
}

/** True when the server changed nothing at all this commit. */
export function commitChangedNothing(result: S["PlantMapCommitResponse"]): boolean {
  const c = result.created;
  return (
    c.facilities +
      c.departments +
      c.work_centers +
      c.storage_locations +
      c.machines +
      result.updated +
      result.deleted ===
    0
  );
}

/**
 * The commit's OUTCOME, read off the 200 body and nothing else (§3.4) — the
 * second line of the ONE success banner (§UX-1 state 4: "success = the §3.8
 * inline banner … toast-scale, not a celebration").
 *
 * It used to be a Card of its own, printed under a green banner that was built
 * from the CLIENT's pre-commit prediction, so one screen could carry "Committed
 * 1 new nodes" above "Updated 1 · archived 0" for the same commit (surface
 * review F2/F6). There is now exactly one announcement and its counts come from
 * the server, including the honest `pending[]` for entities whose owning module
 * is unwired.
 */
export function CommitResult({ result }: { result: S["PlantMapCommitResponse"] }) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const created = result.created;

  const parts: string[] = [];
  if (created.facilities > 0) {
    parts.push(
      t("onboarding.plant.resultPart", {
        count: created.facilities,
        entity: term("facility"),
      }),
    );
  }
  if (created.departments > 0) {
    parts.push(
      t("onboarding.plant.resultPart", {
        count: created.departments,
        entity: term("department"),
      }),
    );
  }
  if (created.work_centers > 0) {
    parts.push(
      t("onboarding.plant.resultPart", {
        count: created.work_centers,
        entity: term("work_center"),
      }),
    );
  }
  if (created.storage_locations > 0) {
    parts.push(
      t("onboarding.plant.resultPart", {
        count: created.storage_locations,
        entity: t("onboarding.plant.diffStorage"),
      }),
    );
  }

  // A SPAN (styled `display: block`), not a `<p>`: this renders inside the
  // banner's own phrasing-content span, and `<p>` is not phrasing content — the
  // HTML parser would close the span before it, breaking both the layout and
  // the `:has()` rule that sizes it.
  return (
    <span className="plant-result num" data-testid="plant-commit-result">
      {commitChangedNothing(result) ? (
        t("onboarding.plant.resultNoChange")
      ) : (
        <>
          {parts.length > 0
            ? t("onboarding.plant.resultCreatedLine", { parts: parts.join(", ") })
            : null}
          {parts.length > 0 ? " " : ""}
          {t("onboarding.plant.resultUpdatedLine", {
            updated: result.updated,
            deleted: result.deleted,
          })}
        </>
      )}
      {result.pending.map((entry) => (
        <span key={entry.entity_type} className="plant-pending" data-testid="plant-pending">
          {" "}
          {t("onboarding.plant.pendingLine", {
            entity: t(`onboarding.plant.pendingEntity.${entry.entity_type}`, {
              machine: term("machine"),
              defaultValue: entry.entity_type,
            }),
            count: entry.count,
            module: t(
              `onboarding.checklist.module.${entry.owner_module.replace(/^module\./, "")}`,
              { defaultValue: entry.owner_module },
            ),
          })}
        </span>
      ))}
    </span>
  );
}

/**
 * "N machines are recorded on this draft — created when Module 08 wires in."
 *
 * Rendered by the SCREEN, beside the tree, for as long as the draft carries
 * machine counts. It is not part of the commit surface: machines are recorded
 * pending and never created, so they are never a reason to commit, and the
 * commit surface correctly disappears the moment the plant matches — taking
 * this sentence with it if it lived there (surface review F4 / doctrine B1).
 */
export function DeferredNote({ machines }: { machines: number }) {
  const { t } = useTranslation();
  const { term } = useTerm();
  if (machines <= 0) return null;
  return (
    <p className="plant-deferred-line" data-testid="plant-diff-deferred">
      {t("onboarding.plant.diffDeferredHeading")}
      {": "}
      {t("onboarding.plant.diffDeferredEntry", {
        entity: t("onboarding.plant.diffMachines", { machine: term("machine") }),
        count: machines,
        module: t("onboarding.checklist.module.08_maintenance", {
          defaultValue: "08_maintenance",
        }),
      })}
    </p>
  );
}
